// Issues & Reports Manager (/volunteer/issues).
//
// ## Built around one loop
//
//   Inbox ─ open the route, find what's wrong, write it down ─▶ In progress
//   In progress ─ a developer fixes it (/admin marks it Fixed) ─▶ Fixed — check
//   Fixed — check ─ reopen the route: it works ─▶ Archive
//                 ─ it still doesn't ─▶ back to In progress, with a note
//   Inbox / In progress ─ nothing is actually wrong ─▶ Archive
//
// Each pile is a tab with its count; the screen opens on the first one with
// anything in it. The Inbox is newest-first (a regression shows up as six rows
// from one afternoon); "Fixed — check" is oldest-fixed-first, because those
// are waiting on YOU.
//
// ## A row is readable closed and workable open
//
// Closed: what the student said, the route (From → To), and the options that
// make it that route. Open: "Open route" (the live map on that exact route,
// accessible/tunnels included), the status, the note for the developer, and
// the one or two buttons that move it to the next pile. The developer's reply
// (`devNote`) sits under the volunteer's note, never over it.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ago, routeLink } from './shared.js';
import { ownSiteUrl, SITE_ORIGINS } from '../utils/safeUrl.js';

const PILES = [
  { id: 'inbox', label: 'Inbox', empty: 'Nothing new. 🎉' },
  { id: 'progress', label: 'In progress', empty: 'Nothing is being investigated right now.' },
  { id: 'fixed', label: 'Fixed — check these', empty: 'Nothing is waiting for a double-check.' },
  { id: 'archived', label: 'Archive', empty: 'Nothing archived yet.' },
];

const STATUS_LABEL = { new: 'Not checked', checking: 'In progress', fixed: 'Fixed', unknown: 'Nothing wrong' };
const STATUS_TONE = { new: 'vt-pill-new', checking: 'vt-pill-warn', fixed: 'vt-pill-good', unknown: 'vt-pill-dim' };

const destOf = (to) => (to ? (to.name || [to.building, to.room].filter(Boolean).join(' ')) : '');
function optionText(o = {}) {
  const t = [];
  if (o.accessible) t.push('Accessible');
  if (o.noElevators) t.push('Stairs only');
  if (o.tunnels) t.push('Tunnels');
  if (o.noTunnels) t.push('No tunnels');
  return t.join(' · ');
}

export default function Issues({ token, onExpired, admin }) {
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState({});
  const [pile, setPile] = useState(null);
  const [kind, setKind] = useState('all');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    const r = await api('/api/issues', { token, onExpired });
    if (!r.ok) { setErr(r.body.error || 'Could not load issues.'); return; }
    setErr('');
    setRows(r.body.issues || []);
    setCounts(r.body.counts || {});
  }, [token, onExpired]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!rows || pile) return;
    setPile(counts.inbox ? 'inbox' : counts.fixed ? 'fixed' : counts.progress ? 'progress' : 'inbox');
  }, [rows, counts, pile]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = (rows || []).filter((r) => r.state === (pile || 'inbox'))
      .filter((r) => kind === 'all' || r.kind === kind)
      .filter((r) => !needle || `${r.message} ${r.from} ${destOf(r.to)} ${r.triage?.note || ''}`.toLowerCase().includes(needle));
    if (pile === 'fixed') list.sort((a, z) => (a.triage?.fixedAt || 0) - (z.triage?.fixedAt || 0));
    return list;
  }, [rows, pile, kind, q]);

  /** One write; the row is replaced from the answer, and moves pile if its state changed. */
  const save = useCallback(async (issue, patch) => {
    setBusy(issue.key); setErr('');
    const r = await api('/api/issues', { method: 'POST', token, onExpired, body: { op: 'triage', key: issue.key, ...patch } });
    setBusy('');
    if (!r.ok) { setErr(r.body.error || 'That did not save.'); return false; }
    setRows((list) => (list || []).map((x) => (x.key === issue.key ? { ...x, triage: r.body.triage, state: r.body.state } : x)));
    setCounts((c) => {
      if (issue.state === r.body.state) return c;
      return { ...c, [issue.state]: Math.max(0, (c[issue.state] || 0) - 1), [r.body.state]: (c[r.body.state] || 0) + 1 };
    });
    if (issue.state !== r.body.state) setOpen('');
    return true;
  }, [token, onExpired]);

  // The operator's own reply, through the same endpoint /admin calls.
  const markFixed = useCallback(async (issue, devNote) => {
    setBusy(issue.key); setErr('');
    const r = await api('/api/issues', { method: 'POST', token, onExpired, body: { op: 'mark-fixed', key: issue.key, devNote } });
    setBusy('');
    if (!r.ok) { setErr(r.body.error || 'That did not save.'); return; }
    await load();
    setOpen('');
  }, [token, onExpired, load]);

  if (!rows) return <div className="vt-empty">{err || 'Loading issues…'}</div>;
  const pileDef = PILES.find((p) => p.id === pile) || PILES[0];

  return (
    <>
      <h1 className="vt-h1">Issues &amp; Reports</h1>
      <p className="vt-sub">
        Problem reports and routes students rated bad. Open the route, write down what’s wrong for the developer,
        and archive it once it’s fixed — or once you’ve found nothing wrong.
      </p>
      <div className="vt-tabs" role="group" aria-label="Pile">
        {PILES.map((p) => (
          <button key={p.id} className="vt-tab" aria-pressed={pile === p.id} onClick={() => { setPile(p.id); setOpen(''); }}>
            {p.label}
            <span className={`vt-count${(p.id === 'inbox' || p.id === 'fixed') && counts[p.id] ? ' vt-count-hot' : ''}`}>{counts[p.id] || 0}</span>
          </button>
        ))}
      </div>
      <div className="vt-toolbar">
        <input className="vt-input" placeholder="Search what they said, a building, a note" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="vt-seg" role="group" aria-label="Kind">
          {[['all', 'All'], ['report', 'Reports'], ['rating', 'Bad routes']].map(([id, label]) => (
            <button key={id} aria-pressed={kind === id} onClick={() => setKind(id)}>{label}</button>
          ))}
        </div>
        <button className="vt-btn" onClick={load}>Refresh</button>
      </div>
      {err && <div className="vt-err">{err}</div>}
      {!shown.length ? <div className="vt-empty">{q ? 'Nothing matches that.' : pileDef.empty}</div> : (
        <div className="vt-list">
          {shown.map((r) => (
            <IssueRow key={r.key} r={r} open={open === r.key} busy={busy === r.key} admin={admin}
                      onToggle={() => setOpen(open === r.key ? '' : r.key)} onSave={(patch) => save(r, patch)}
                      onMarkFixed={(devNote) => markFixed(r, devNote)} />
          ))}
        </div>
      )}
    </>
  );
}

function IssueRow({ r, open, busy, admin, onToggle, onSave, onMarkFixed }) {
  const t = r.triage || {};
  const status = t.status || 'new';
  const [note, setNote] = useState(t.note || '');
  const [devNote, setDevNote] = useState(t.devNote || '');
  useEffect(() => { setNote(t.note || ''); }, [t.note, open]);
  const link = routeLink(r);
  // The page the student was on — which the report records itself, so anybody
  // posting straight to /api/report can put any address here. Only a page on
  // this site becomes a link; anything else is shown as text, never clickable.
  const pageUrl = ownSiteUrl(r.url, [window.location.origin, ...SITE_ORIGINS]);
  const dirty = note.trim() !== (t.note || '').trim();
  const withNote = (patch) => onSave(dirty ? { ...patch, note } : patch);

  return (
    <div className={`vt-row${open ? ' vt-row-hl' : ''}`}>
      <div className="vt-rowhead" onClick={onToggle} style={{ cursor: 'pointer' }}
           role="button" tabIndex={0} aria-expanded={open}
           onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onToggle(); } }}>
        <span className={`vt-pill ${r.kind === 'report' ? 'vt-pill-new' : 'vt-pill-bad'}`} style={{ marginTop: 2 }}>
          {r.kind === 'report' ? 'Report' : 'Bad route'}
        </span>
        <div className="vt-grow">
          <div className={`vt-issue-msg${open ? '' : ' vt-clamp'}`}>
            {r.message ? `“${r.message}”` : <i className="vt-meta">No comment — just rated the route bad.</i>}
          </div>
          <div className="vt-meta vt-route">
            {r.from || '—'} → {destOf(r.to) || '—'}
          </div>
          <div className="vt-meta">
            {[optionText(r.options), r.meters != null ? `${r.meters} m` : '', r.kind === 'report' && !r.routeFound ? 'no route found' : '',
              r.source, ago(r.ts)].filter(Boolean).join(' · ')}
          </div>
          {!open && t.note && <div className="vt-meta vt-clamp" style={{ color: 'var(--ink)' }}>📝 {t.note}</div>}
        </div>
        <span className={`vt-pill ${STATUS_TONE[status]}`}>{STATUS_LABEL[status]}</span>
      </div>

      {open && (
        <div className="vt-rowbody">
          <div className="vt-actions" style={{ justifyContent: 'flex-start', marginBottom: 12 }}>
            {link
              ? <a className="vt-btn vt-primary" href={link} target="_blank" rel="noreferrer">Open route ↗</a>
              : <span className="vt-meta">No destination room was recorded, so there is no route to reopen.</span>}
            {/* The same route in the Route tester (/volunteer/routes), same
                tab: it reads the four query params /umanitoba does, so the
                report's From, To and options arrive as the Start, End and
                variant, with every node on the path drawn. */}
            {link && (
              <a className="vt-btn" href={`/volunteer/routes${new URL(link).search}`}>Test route 🧪</a>
            )}
            {pageUrl && <a className="vt-btn" href={pageUrl} target="_blank" rel="noreferrer">Page they were on ↗</a>}
            {r.url && !pageUrl && <span className="vt-meta" style={{ wordBreak: 'break-all' }}>Page (not on UMaps, not linked): {String(r.url)}</span>}
          </div>

          {r.kind === 'report' && (r.device || r.stage || r.viewport) && (
            <div className="vt-meta" style={{ marginBottom: 10 }}>
              {[r.device, r.stage && `stage: ${r.stage}`, r.viewport && `screen ${r.viewport}`].filter(Boolean).join(' · ')}
            </div>
          )}

          <div className="vt-field">
            <label className="vt-label" htmlFor={`note-${r.key}`}>What’s wrong (for the developer)</label>
            <textarea id={`note-${r.key}`} className="vt-textarea" value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder="e.g. “The route uses the Armes tunnel door, which is locked after 6pm. Should route via the Machray entrance.”" />
            {t.updatedAt ? <div className="vt-hint">Last updated {ago(t.updatedAt)}{t.by ? ` by ${t.by}` : ''}</div> : null}
          </div>

          {t.devNote && (
            <div className="vt-devnote">
              <b>Developer:</b> {t.devNote}
              {t.fixedAt ? <div className="vt-hint" style={{ color: 'inherit' }}>Marked fixed {ago(t.fixedAt)}{t.fixedBy ? ` by ${t.fixedBy}` : ''}</div> : null}
            </div>
          )}
          {!t.devNote && t.fixedAt && (
            <div className="vt-devnote">Marked fixed {ago(t.fixedAt)}{t.fixedBy ? ` by ${t.fixedBy}` : ''}.</div>
          )}

          {admin && r.state !== 'archived' && (
            <div className="vt-field" style={{ marginTop: 12 }}>
              <label className="vt-label" htmlFor={`dev-${r.key}`}>Developer reply (operator only)</label>
              <textarea id={`dev-${r.key}`} className="vt-textarea" rows={2} value={devNote} onChange={(e) => setDevNote(e.target.value)}
                        placeholder="What you changed, so the volunteer knows what to re-test." />
              <button className="vt-btn vt-good vt-small" style={{ marginTop: 6 }} disabled={busy}
                      onClick={() => onMarkFixed(devNote.trim())}>Mark fixed as developer</button>
            </div>
          )}

          <div className="vt-actions" style={{ justifyContent: 'flex-start', marginTop: 12 }}>
            {r.state === 'inbox' && (
              <>
                <button className="vt-btn vt-primary" disabled={busy} onClick={() => withNote({ status: 'checking' })}>
                  {dirty ? 'Save note · In progress' : 'Start investigating'}
                </button>
                <button className="vt-btn" disabled={busy} onClick={() => withNote({ status: 'unknown', archived: true })}>Nothing wrong — archive</button>
              </>
            )}
            {r.state === 'progress' && (
              <>
                {dirty && <button className="vt-btn vt-primary" disabled={busy} onClick={() => onSave({ note })}>Save note</button>}
                <button className="vt-btn vt-good" disabled={busy} onClick={() => withNote({ status: 'fixed' })}>I fixed it — mark fixed</button>
                <button className="vt-btn" disabled={busy} onClick={() => withNote({ status: 'unknown', archived: true })}>Nothing wrong — archive</button>
              </>
            )}
            {r.state === 'fixed' && (
              <>
                <button className="vt-btn vt-good" disabled={busy} onClick={() => withNote({ archived: true })}>Checked — it works, archive</button>
                <button className="vt-btn vt-danger" disabled={busy || !note.trim()}
                        title={note.trim() ? '' : 'Say what is still wrong first'}
                        onClick={() => onSave({ status: 'checking', note })}>Still broken</button>
                {dirty && <button className="vt-btn" disabled={busy} onClick={() => onSave({ note })}>Save note</button>}
              </>
            )}
            {r.state === 'archived' && (
              <button className="vt-btn" disabled={busy} onClick={() => withNote({ archived: false, ...(status === 'unknown' ? { status: 'checking' } : {}) })}>
                Reopen
              </button>
            )}
            <label className="vt-meta" style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
              Status
              <select className="vt-select" style={{ width: 'auto', padding: '5px 8px' }} value={status} disabled={busy}
                      onChange={(e) => withNote({ status: e.target.value })}>
                {Object.entries(STATUS_LABEL).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
              </select>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
